<template>
  <!-- eslint-disable vue/max-attributes-per-line -->
  <div>
    <v-breadcrumbs :items="breadcrumbs" divider="/" class="pa-0 mb-4">
      <template #item="{ item }">
        <v-breadcrumbs-item :to="item.to" :disabled="item.disabled" exact>
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>

    <!-- Page header -->
    <div class="d-flex align-start mb-6 flex-wrap" style="gap: 12px">
      <div class="flex-grow-1">
        <p class="text-2xl mb-1">
          Льготы
        </p>
        <p class="text--secondary mb-0">
          Заявки пользователей.
        </p>
      </div>
      <div class="d-flex align-center flex-wrap" style="gap: 8px">
        <v-btn
          v-if="section === 'user-benefits'"
          color="success"
          small
          @click="openCreateBenefitModal"
        >
          <v-icon left small>
            {{ mdiPlus }}
          </v-icon>
          Новая льгота
        </v-btn>
      </div>
    </div>

    <v-tabs class="mb-5">
      <v-tab to="/benefits/requests" exact>
        Заявки
      </v-tab>
      <v-tab to="/benefits/user-benefits" exact>
        Льготы пользователей
      </v-tab>
      <v-tab to="/benefits/logs" exact>
        Логи
      </v-tab>
    </v-tabs>

    <v-card v-if="sectionSwitchLoading" flat outlined class="mb-5 pa-4">
      <v-progress-linear indeterminate color="primary" class="mb-4" />
      <v-skeleton-loader type="table" />
    </v-card>

    <!-- ========== REQUESTS ========== -->
    <div v-show="section === 'requests' && !sectionSwitchLoading">
      <benefits-kpi-bar
        :stats="reqStats"
        :active-status="reqFilters.status"
        class="mb-2"
        @pick="onKpiPick"
      />

      <benefits-toolbar
        v-model="reqFilters"
        :loading="reqLoading"
        :status-items="requestStatusItems"
        show-search
        show-status
        show-date-range
        search-label="Поиск по имени"
        date-from-label="Создана с"
        date-to-label="Создана по"
        @apply="onReqFiltersApply"
      />

      <v-card>
        <v-data-table
          :headers="requestHeaders"
          :items="displayedRequests"
          :loading="reqLoading"
          :items-per-page="-1"
          class="elevation-0"
          hide-default-footer
        >
          <template #[`item.created_at`]="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template #[`item.row_number`]="{ item }">
            <v-btn text small color="primary" @click="openRequestProcess(item)">
              {{ item.row_number }}
            </v-btn>
          </template>

          <template #[`item.user`]="{ item }">
            <div>
              <div class="font-weight-medium">
                {{ item.user_first_name || '—' }}
              </div>
              <div class="text-caption text--secondary">
                {{ item.source || '' }}
              </div>
            </div>
          </template>

          <template #[`item.requested_category_name`]="{ item }">
            <v-chip v-if="item.requested_category_name" x-small outlined label>
              {{ item.requested_category_name }}
            </v-chip>
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.status`]="{ item }">
            <div class="py-2">
              <benefit-status-chip :status="item.status" />
            </div>
          </template>

          <template #[`item.file_url`]="{ item }">
            <benefit-document-preview :url="item.file_url" />
          </template>
        </v-data-table>

        <v-divider v-if="reqMeta.num_pages" />
        <benefits-paginator
          v-if="reqMeta.num_pages"
          :meta="reqMeta"
          :page="reqPage"
          :page-size="reqPageSize"
          :loading="reqLoading"
          @page="onReqPage"
          @page-size="onReqPageSize"
        />
      </v-card>
    </div>

    <!-- ========== USER BENEFITS ========== -->
    <div v-show="section === 'user-benefits' && !sectionSwitchLoading">
      <benefits-toolbar
        v-model="ubFilters"
        :loading="ubLoading"
        :status-items="userBenefitStatusItems"
        show-search
        show-status
        show-date-range
        search-label="Поиск по ФИО, телефону или email"
        date-from-label="Создана с"
        date-to-label="Создана по"
        @apply="onUbFiltersApply"
      >
        <template #extra-filters="{ filters }">
          <v-col cols="12" sm="3">
            <v-select
              v-model="filters.category_23_24_mode"
              :items="category2324ModeItems"
              item-text="text"
              item-value="value"
              label="Категории 23 и 24"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="filters.category_id"
              :items="categoryItems"
              item-text="name"
              item-value="id"
              label="Категория"
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
        </template>
      </benefits-toolbar>

      <v-card>
        <v-data-table
          :headers="userBenefitHeaders"
          :items="displayedUserBenefits"
          :loading="ubLoading"
          :items-per-page="-1"
          class="elevation-0"
          hide-default-footer
        >
          <template #[`item.row_number`]="{ item }">
            <v-btn text small color="primary" @click="openUserBenefitProcess(item)">
              {{ item.row_number }}
            </v-btn>
          </template>

          <template #[`item.user`]="{ item }">
            <div>
              <div class="font-weight-medium">
                {{ formatUserName(item) }}
              </div>
              <div class="text-caption text--secondary">
                {{ item.user_phone || item.user_email || '' }}
              </div>
            </div>
          </template>

          <template #[`item.category_name`]="{ item }">
            {{ item.category_name || '—' }}
          </template>

          <template #[`item.status`]="{ item }">
            <benefit-status-chip :status="item.status" />
          </template>
        </v-data-table>
        <v-divider v-if="ubMeta.num_pages" />
        <benefits-paginator
          v-if="ubMeta.num_pages"
          :meta="ubMeta"
          :page="ubPage"
          :page-size="ubPageSize"
          :loading="ubLoading"
          @page="onUbPage"
          @page-size="onUbPageSize"
        />
      </v-card>
    </div>

    <!-- ========== LOGS ========== -->
    <div v-show="section === 'logs' && !sectionSwitchLoading">
      <benefits-toolbar
        v-model="logFilters"
        :loading="logLoading"
        :status-items="logActionItems"
        status-label="Действие"
        show-status
        show-date-range
        search-label="Поиск"
        date-from-label="Дата с"
        date-to-label="Дата по"
        @apply="onLogFiltersApply"
      >
        <template #extra-filters="{ filters }">
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="filters.user_id"
              label="user_id"
              clearable
              dense
              outlined
              hide-details
              @keydown.enter="onLogFiltersApply(filters)"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="filters.benefit_id"
              label="benefit_id"
              clearable
              dense
              outlined
              hide-details
              @keydown.enter="onLogFiltersApply(filters)"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="filters.request_id"
              label="request_id"
              clearable
              dense
              outlined
              hide-details
              @keydown.enter="onLogFiltersApply(filters)"
            />
          </v-col>
        </template>
      </benefits-toolbar>

      <v-card>
        <v-data-table
          :headers="logHeaders"
          :items="logs"
          :loading="logLoading"
          :items-per-page="-1"
          class="elevation-0"
          hide-default-footer
        >
          <template #[`item.action_display`]="{ item }">
            <benefit-log-action-chip :action="item.action" />
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </v-data-table>

        <v-divider v-if="logMeta.num_pages" />
        <benefits-paginator
          v-if="logMeta.num_pages"
          :meta="logMeta"
          :page="logPage"
          :page-size="logPageSize"
          :loading="logLoading"
          @page="onLogPage"
          @page-size="onLogPageSize"
        />
      </v-card>
    </div>

    <!-- ========== CREATE BENEFIT DIALOG ========== -->
    <v-dialog v-model="createBenefitDialog" max-width="760">
      <v-card>
        <v-card-title class="pb-2">
          Создать новую льготу
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-autocomplete
            v-model="createBenefit.user_id"
            :items="userSearchItems"
            :loading="userSearchLoading"
            :search-input.sync="userSearchQuery"
            item-text="label"
            item-value="id"
            hide-no-data
            hide-selected
            clearable
            dense
            outlined
            label="Пользователь (поиск по имени / телефону / email)"
            class="mb-3"
            @update:search-input="onUserSearchInput"
          />
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="createBenefit.category_id"
                :items="categoryItems"
                item-text="name"
                item-value="id"
                label="Категория льготы"
                dense
                outlined
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field :value="createBenefit.source" label="Источник" readonly dense outlined />
            </v-col>
          </v-row>
          <v-row dense class="mb-1">
            <v-col cols="12" sm="6">
              <v-menu v-model="createMenus.activeFrom" :close-on-content-click="false" offset-y>
                <template #activator="{ on, attrs }">
                  <v-text-field v-model="createBenefit.active_from" label="Действует с" readonly clearable dense outlined hide-details v-bind="attrs" v-on="on" />
                </template>
                <v-date-picker v-model="createBenefit.active_from" @input="createMenus.activeFrom = false" />
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu v-model="createMenus.activeTo" :close-on-content-click="false" offset-y>
                <template #activator="{ on, attrs }">
                  <v-text-field v-model="createBenefit.active_to" label="Действует по" readonly clearable dense outlined hide-details v-bind="attrs" v-on="on" />
                </template>
                <v-date-picker v-model="createBenefit.active_to" @input="createMenus.activeTo = false" />
              </v-menu>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-textarea v-model="createBenefit.comment" label="Комментарий" rows="2" dense outlined />
            </v-col>
            <v-col cols="12">
              <v-file-input v-model="createBenefit.document" label="Документ" dense outlined show-size />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="createBenefitDialog = false">
            Отмена
          </v-btn>
          <v-btn color="primary" :loading="createBenefitLoading" @click="submitCreateBenefit">
            Создать
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">
      {{ snackbarText }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import {
  mdiPlus,
  mdiHistory,
  mdiClose,
} from '@mdi/js'
import Api from '@/api/api'
import { stripEmptyParams, formatDateTime } from './utils'
import BenefitsKpiBar from './components/BenefitsKpiBar.vue'
import BenefitsToolbar from './components/BenefitsToolbar.vue'
import BenefitStatusChip from './components/BenefitStatusChip.vue'
import BenefitDocumentPreview from './components/BenefitDocumentPreview.vue'
import BenefitLogActionChip from './components/BenefitLogActionChip.vue'
import BenefitsPaginator from './components/BenefitsPaginator.vue'

export default {
  name: 'Benefits',
  components: {
    BenefitsKpiBar,
    BenefitsToolbar,
    BenefitStatusChip,
    BenefitDocumentPreview,
    BenefitLogActionChip,
    BenefitsPaginator,
  },
  data() {
    return {
      mdiPlus,
      mdiHistory,
      mdiClose,

      snackbar: false,
      snackbarText: '',
      snackbarColor: 'primary',

      // requests
      reqLoading: false,
      requests: [],
      reqPage: 1,
      reqPageSize: 20,
      reqFilters: {
        search: '',
        status: '',
        date_from: '',
        date_to: '',
      },
      requestStatusItems: [
        { text: 'На проверке', value: 'pending' },
        { text: 'Одобрена', value: 'approved' },
        { text: 'Отклонена', value: 'rejected' },
      ],
      reqMeta: {
        count: 0,
        num_pages: 0,
        current_page: 1,
        has_next: false,
        has_previous: false,
      },
      requestHeaders: [
        {
          text: '#', value: 'row_number', width: 60, sortable: false,
        },
        { text: 'Дата', value: 'created_at', width: 140 },
        { text: 'Пользователь', value: 'user', sortable: false },
        {
          text: 'Статус', value: 'status', sortable: false, width: 190,
        },
        { text: 'Документ', value: 'file_url', sortable: false },
      ],

      // user-benefits
      ubLoading: false,
      userBenefits: [],
      ubPage: 1,
      ubPageSize: 50,
      ubFilters: {
        search: '',
        status: '',
        category_23_24_mode: 'hide',
        category_id: '',
        date_from: '',
        date_to: '',
      },
      category2324ModeItems: [
        { text: 'Все', value: 'all' },
        { text: 'Скрыть (по умолчанию)', value: 'hide' },
        { text: 'Показать', value: 'show' },
      ],
      userBenefitStatusItems: [
        { text: 'Активна', value: 'active' },
        { text: 'Неактивна', value: 'inactive' },
        { text: 'Истекла', value: 'expired' },
      ],
      ubMeta: {
        count: 0,
        num_pages: 0,
        current_page: 1,
        has_next: false,
        has_previous: false,
      },
      userBenefitHeaders: [
        {
          text: '№', value: 'row_number', width: 80, sortable: false,
        },
        { text: 'Пользователь', value: 'user', sortable: false },
        { text: 'Категория льготы', value: 'category_name', sortable: false },
        {
          text: 'Статус', value: 'status', width: 160, sortable: false,
        },
      ],

      // logs
      logLoading: false,
      logs: [],
      logPage: 1,
      logPageSize: 20,
      logFilters: {
        search: '',
        user_id: '',
        benefit_id: '',
        request_id: '',
        action: '',
        date_from: '',
        date_to: '',
      },
      logActionItems: [
        { text: 'Создано', value: 'created' },
        { text: 'Обновлено', value: 'updated' },
        { text: 'Одобрено', value: 'approved' },
        { text: 'Отклонено', value: 'rejected' },
        { text: 'Активировано', value: 'activated' },
        { text: 'Истекло', value: 'expired' },
        { text: 'Удалено', value: 'deleted' },
      ],
      logMeta: { count: 0, num_pages: 0, current_page: 1 },
      logHeaders: [
        { text: 'ID', value: 'id', width: 72 },
        { text: 'Действие', value: 'action_display', sortable: false },
        { text: 'user_id', value: 'user_id' },
        { text: 'Имя', value: 'user_first_name' },
        { text: 'benefit_id', value: 'user_benefit_id' },
        { text: 'request_id', value: 'benefit_request_id' },
        { text: 'Кто', value: 'performed_by_name' },
        { text: 'Комментарий', value: 'comment' },
        { text: 'Время', value: 'created_at' },
      ],

      // categories
      categoryItems: [],
      categoriesLoading: false,

      // create benefit dialog
      createBenefitDialog: false,
      createBenefitLoading: false,
      createBenefit: {
        user_id: '',
        category_id: '',
        source: 'admin',
        active_from: '',
        active_to: '',
        comment: '',
        document: null,
      },
      createMenus: { activeFrom: false, activeTo: false },
      userSearchQuery: '',
      userSearchItems: [],
      userSearchLoading: false,
      userSearchTimeout: null,
      sectionSwitchLoading: false,

    }
  },
  computed: {
    section() {
      return this.$route.params.section || 'requests'
    },
    sectionTitle() {
      const map = {
        requests: 'Заявки',
        'user-benefits': 'Льготы пользователей',
        logs: 'Логи',
      }
      return map[this.section] || 'Заявки'
    },
    breadcrumbs() {
      return [
        { text: 'Льготы', to: '/benefits/requests', disabled: false },
        { text: this.sectionTitle, disabled: true },
      ]
    },
    displayedRequests() {
      return this.requests.map((item, index) => ({
        ...item,
        row_number: ((this.reqPage - 1) * this.reqPageSize) + index + 1,
      }))
    },
    displayedUserBenefits() {
      return this.userBenefits.map((item, index) => ({
        ...item,
        row_number: ((this.ubPage - 1) * this.ubPageSize) + index + 1,
      }))
    },
    reqStats() {
      const counts = { pending: 0, approved: 0, rejected: 0 }
      this.requests.forEach(r => {
        if (counts[r.status] !== undefined) counts[r.status] += 1
      })
      return counts
    },
  },
  watch: {
    '$route.params.section': function onRouteSectionWatch(to, from) {
      const changedSection = Boolean(from) && to !== from
      this.onRouteSectionChange({ showSwitchLoader: changedSection })
    },
  },
  async created() {
    await this.loadCategories()
    await this.onRouteSectionChange({ showSwitchLoader: false })
  },
  methods: {
    formatDateTime,

    async onRouteSectionChange({ showSwitchLoader = false } = {}) {
      if (this.$route.name !== 'Льготы') return
      const s = this.section
      if (showSwitchLoader) this.sectionSwitchLoading = true
      try {
        if (s === 'requests') {
          await this.loadRequests()
        } else if (s === 'user-benefits') {
          await this.loadUserBenefits()
        } else if (s === 'logs') {
          const q = this.$route.query || {}
          if (q.request_id) this.logFilters.request_id = q.request_id
          if (q.benefit_id) this.logFilters.benefit_id = q.benefit_id
          if (q.user_id) this.logFilters.user_id = q.user_id
          await this.loadLogs()
        }
      } finally {
        this.sectionSwitchLoading = false
      }
    },

    toast(msg, color = 'primary') {
      this.snackbarText = msg
      this.snackbarColor = color
      this.snackbar = true
    },

    apiError(err, fallback = 'Ошибка запроса') {
      const d = err.response && err.response.data
      const msg = (d && (d.error || d.message)) || (d && d.data && d.data.error) || err.message || fallback
      this.toast(msg, 'error')
    },

    // --- Requests ---
    onReqFiltersApply(filters) {
      this.reqFilters = filters
      this.reqPage = 1
      this.loadRequests()
    },

    onKpiPick(status) {
      this.reqFilters = { ...this.reqFilters, status }
      this.reqPage = 1
      this.loadRequests()
    },

    onReqPage(page) {
      this.reqPage = page
      this.loadRequests()
    },

    onReqPageSize(size) {
      this.reqPageSize = size
      this.reqPage = 1
      this.loadRequests()
    },

    async loadRequests() {
      this.reqLoading = true
      try {
        const params = stripEmptyParams({
          status: this.reqFilters.status,
          created_at_from: this.reqFilters.date_from,
          created_at_to: this.reqFilters.date_to,
          search: this.reqFilters.search,
          page: this.reqPage,
          page_size: this.reqPageSize,
        })
        const res = await Api.getBenefitRequests(params)
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить заявки', 'error')
          return
        }
        const data = res.data || {}
        this.requests = data.requests || []
        this.reqMeta = {
          count: data.count || 0,
          num_pages: data.num_pages || 0,
          current_page: data.current_page || this.reqPage,
          has_next: !!data.has_next,
          has_previous: !!data.has_previous,
        }
      } catch (e) {
        this.apiError(e)
      } finally {
        this.reqLoading = false
      }
    },

    openRequestProcess(item) {
      this.$router.push({ path: `/benefits/requests/${item.id}` })
    },

    openRequestLogs(item) {
      this.logFilters = { ...this.logFilters, request_id: String(item.id) }
      this.$router.push({ path: '/benefits/logs', query: { request_id: String(item.id) } })
    },

    // --- User benefits ---
    onUbFiltersApply(filters) {
      this.ubFilters = this.normalizeUbFilters(filters)
      this.ubPage = 1
      this.loadUserBenefits()
    },

    onUbPage(page) {
      this.ubPage = page
      this.loadUserBenefits()
    },

    onUbPageSize(size) {
      this.ubPageSize = Number(size) || 50
      this.ubPage = 1
      this.loadUserBenefits()
    },

    async loadUserBenefits() {
      this.ubLoading = true
      try {
        const params = stripEmptyParams({
          search: this.ubFilters.search,
          status: this.ubFilters.status,
          category_23_24_mode: this.ubFilters.category_23_24_mode || 'hide',
          category_id: this.ubFilters.category_id,
          created_at_from: this.ubFilters.date_from,
          created_at_to: this.ubFilters.date_to,
          page: this.ubPage,
          page_size: this.ubPageSize,
        })
        const res = await Api.getUserBenefitsList(params)
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить льготы', 'error')
          return
        }
        const data = res.data || {}
        this.userBenefits = data.items || []
        this.ubMeta = {
          count: data.count || 0,
          num_pages: data.num_pages || 0,
          current_page: data.current_page || this.ubPage,
          has_next: !!data.has_next,
          has_previous: !!data.has_previous,
        }
      } catch (e) {
        this.apiError(e)
      } finally {
        this.ubLoading = false
      }
    },

    openUserBenefitProcess(item) {
      if (!item || !item.id) return
      this.$router.push({ path: `/benefits/user-benefits/${item.id}` })
    },

    formatUserName(item) {
      if (!item) return '—'
      const full = [item.user_last_name, item.user_first_name].filter(Boolean).join(' ').trim()
      return full || item.user_email || `ID ${item.user_id}`
    },

    normalizeUbFilters(filters) {
      const safe = { ...(filters || {}) }
      if (!['all', 'hide', 'show'].includes(safe.category_23_24_mode)) {
        safe.category_23_24_mode = 'hide'
      }
      return safe
    },

    // --- Logs ---
    onLogPage(page) {
      this.logPage = page
      this.loadLogs()
    },
    onLogFiltersApply(filters) {
      this.logFilters = { ...filters }
      this.logPage = 1
      this.loadLogs()
    },

    onLogPageSize(size) {
      this.logPageSize = size
      this.logPage = 1
      this.loadLogs()
    },

    async loadLogs() {
      this.logLoading = true
      try {
        const params = stripEmptyParams({
          search: this.logFilters.search,
          action: this.logFilters.status || this.logFilters.action,
          user_id: this.logFilters.user_id,
          benefit_id: this.logFilters.benefit_id,
          request_id: this.logFilters.request_id,
          date_from: this.logFilters.date_from,
          date_to: this.logFilters.date_to,
          page: this.logPage,
          page_size: this.logPageSize,
        })
        const res = await Api.getBenefitLogs(params)
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить логи', 'error')
          return
        }
        const data = res.data || {}
        this.logs = data.logs || []
        this.logMeta = {
          count: data.count || 0,
          num_pages: data.num_pages || 0,
          current_page: data.current_page || this.logPage,
        }
      } catch (e) {
        this.apiError(e)
      } finally {
        this.logLoading = false
      }
    },

    // --- Categories ---
    async loadCategories() {
      this.categoriesLoading = true
      try {
        const res = await Api.getBenefitCategories()
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить категории', 'error')
          return
        }
        this.categoryItems = res.data || []
      } catch (e) {
        this.apiError(e)
      } finally {
        this.categoriesLoading = false
      }
    },

    // --- Create benefit ---
    openCreateBenefitModal() {
      this.createBenefit = {
        user_id: '',
        category_id: '',
        source: 'admin',
        active_from: '',
        active_to: '',
        comment: '',
        document: null,
      }
      this.userSearchQuery = ''
      this.userSearchItems = []
      this.createBenefitDialog = true
    },

    onUserSearchInput(value) {
      if (this.userSearchTimeout) clearTimeout(this.userSearchTimeout)
      this.userSearchTimeout = setTimeout(() => this.fetchUsers(value), 350)
    },

    async fetchUsers(query) {
      const q = String(query || '').trim()
      if (!q || q.length < 2) {
        this.userSearchItems = []
        return
      }
      this.userSearchLoading = true
      try {
        const res = await Api.searchBenefitUsers({ query: q, limit: 20 })
        if (!res.success) { this.userSearchItems = []; return }
        this.userSearchItems = (res.data || []).map(item => ({
          ...item,
          label: `${item.id} — ${item.first_name || ''} ${item.last_name || ''} (${item.phone || item.email || ''})`,
        }))
      } catch (e) {
        this.userSearchItems = []
      } finally {
        this.userSearchLoading = false
      }
    },

    async submitCreateBenefit() {
      if (!this.createBenefit.user_id || !this.createBenefit.category_id) {
        this.toast('Укажите пользователя и категорию', 'error')
        return
      }
      this.createBenefitLoading = true
      try {
        const hasFile = this.createBenefit.document instanceof File
        let payload
        if (hasFile) {
          payload = new FormData()
          payload.append('user_id', String(this.createBenefit.user_id))
          payload.append('category_id', String(this.createBenefit.category_id))
          payload.append('source', 'admin')
          if (this.createBenefit.active_from) payload.append('active_from', this.createBenefit.active_from)
          if (this.createBenefit.active_to) payload.append('active_to', this.createBenefit.active_to)
          if (this.createBenefit.comment) payload.append('comment', this.createBenefit.comment)
          payload.append('document', this.createBenefit.document)
        } else {
          payload = stripEmptyParams({
            user_id: this.createBenefit.user_id,
            category_id: this.createBenefit.category_id,
            source: 'admin',
            active_from: this.createBenefit.active_from || undefined,
            active_to: this.createBenefit.active_to || undefined,
            comment: this.createBenefit.comment || undefined,
          })
        }
        const res = await Api.createUserBenefit(payload)
        if (res.success === false) {
          this.toast(res.error || 'Ошибка создания', 'error')
          return
        }
        this.toast(res.message || 'Льгота создана', 'success')
        this.createBenefitDialog = false
        await this.loadUserBenefits()
      } catch (e) {
        this.apiError(e)
      } finally {
        this.createBenefitLoading = false
      }
    },

  },
}
</script>
